import React, {useEffect, useLayoutEffect, useMemo} from 'react';
import {Box, Grid} from '@mui/material';
import {AutoField, SelectGroupOption} from '../../../components/AutoForm/AutoFieldNew';
import {PotDetails} from '../../../schema-types/PotDetails';
import useWithdrawalReason from '../../../hooks/useWithdrawalReason';
import FREQUENCY from '../../../constants/Frequency';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {
  withdrawalAmountField as builderAmountField,
  withdrawalFieldsType,
  withdrawalFrequencyField,
  withdrawalFullField,
  withdrawalReasonField,
  withdrawalStartField,
} from './builderWithdrawalFields';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {FULL_SURRENDER, getWithdrawalReasonList} from './builderWithdrawalFormFields';
import {GetWithdrawalReason_getCuroCodes} from '../../../schema-types/GetWithdrawalReason';
import useComboFrequency from '../../../hooks/useComboFrequency';
import {
  setStartDateMustBe10daysInTheFuture,
  setWithdrawalFullAmountFactory,
} from './BuilderWithdrawalSourcesHelpers';
import {useBuilderContext} from '../CreateBuilder/useBuilderContext';
import {useAutoForm, useAutoField} from '../../../components/AutoForm/useAutoForm';
import {newUTCDate} from '../../../utils/newUTCDate';
import {
  handleWithdrawalFullAndReason,
  useHandleWithdrawalFullAndReason,
} from '../hooks/builderWithdrawalHooks';

export interface WithdrawalExistingPlan {
  // eslint-disable-next-line react/no-unused-prop-types
  id?: string;
  maxAmount?: number;
  // eslint-disable-next-line react/no-unused-prop-types
  index?: number;
  pot: PotDetails;
}

export {handleWithdrawalFullAndReason, useHandleWithdrawalFullAndReason};

export const WithdrawalDetails = ({
  maxAmount: totalAmount,
  pot,
}: WithdrawalExistingPlan) => {
  const {values, setFieldValue} = useAutoForm<builderFieldsType>();
  const {builderDetails} = useBuilderContext();
  const [amountField, _, {setValue: setAmount}] = useAutoField<
    withdrawalFieldsType['source']['singleClientAmount']
  >('withdraw.source.singleClientAmount');
  const withdrawalValues = values.withdraw!;
  // const [options, setOptions] = useState<SelectGroupOption[]>([]);
  // const [previousDate, setPreviousDate] = useState(
  //   getISODateOrEmptyString(builderDetails?.startDate)?.split('T')?.[0]
  // );
  const {withdrawalReason: withdrawalReasonData} = useWithdrawalReason();
  const withdrawFrequency = values.withdraw?.source?.frequency;
  const hasSingle = withdrawFrequency === FREQUENCY.SINGLE;
  const hasRecurring = !!withdrawFrequency && withdrawFrequency !== FREQUENCY.SINGLE;

  const setFullAmount = setWithdrawalFullAmountFactory({
    withdrawalValues,
    totalAmount,
    setAmount,
  });

  const planNeedsCrystallised = pot.productName?.formattedName
    ?.toLocaleLowerCase()
    .includes('sipp-drawdown');

  const options = useMemo(() => {
    // set withdrawal options depending on frequency and if its full withdrawal
    if (!withdrawalReasonData) {
      return [];
    }
    if (!values.withdraw?.source?.frequency) {
      return [];
    }
    const isSingleFrequency = values.withdraw?.source?.frequency === FREQUENCY.SINGLE;
    const transferReasonList = getWithdrawalReasonList(
      withdrawalReasonData as GetWithdrawalReason_getCuroCodes[],
      isSingleFrequency,
      withdrawalValues.source.full || false,
      values,
      pot
    );

    return transferReasonList;
  }, [
    withdrawalReasonData,
    values.withdraw?.source?.frequency,
    values.planAction,
    values.mainPlanType,
    withdrawalValues.source.full,
  ]);

  useHandleWithdrawalFullAndReason({options, setFullAmount});

  useEffect(() => {
    setStartDateMustBe10daysInTheFuture({
      today: newUTCDate(),
      withdrawalValues,
      values,
      setFieldValue,
      builderDetails,
    });
  }, [withdrawalValues.source.start, values.withdraw?.source?.frequency, builderDetails]);

  return (
    <React.Fragment>
      <Box mt={1}>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Grid container direction="row" spacing={2}>
              <Grid item xs={6}>
                <AutoField
                  test="Reason"
                  field={{...withdrawalReasonField, optionGroups: options}}
                  disabled={withdrawalValues.source.full}
                />
              </Grid>
              <Grid item xs={6}>
                <AutoField
                  test="Frequency"
                  field={withdrawalFrequencyField}
                  disabled={withdrawalValues.source.full}
                />
              </Grid>
              {planNeedsCrystallised ? (
                <Grid item xs={6}>
                  <AutoField
                    test="Uncrystallised"
                    field={{
                      ...builderAmountField,
                      readOnly: true,
                      id: 'withdraw.source.uncrystallisedFunds',
                      label: 'Uncrystallised',
                    }}
                  />
                </Grid>
              ) : null}
              {planNeedsCrystallised ? (
                <Grid item xs={6}>
                  <AutoField
                    test="Crystallised"
                    field={{
                      ...builderAmountField,
                      readOnly: true,
                      id: 'withdraw.source.crystallisedFunds',
                      label: 'Crystallised',
                    }}
                  />
                </Grid>
              ) : null}

              {hasSingle && withdrawalValues.source.reason !== 'Max Tax Free Cash' ? (
                <Grid item xs={6}>
                  <AutoField
                    test="Amount"
                    field={{
                      ...builderAmountField,
                      label: 'Single Withdrawal Amount',
                      id: 'withdraw.source.singleClientAmount',
                      disabled: withdrawalValues.source.reason === 'Max Tax Free Cash',
                    }}
                  />
                </Grid>
              ) : null}

              {hasRecurring && (
                <Grid item xs={6}>
                  <AutoField
                    test="Amount"
                    field={{
                      ...builderAmountField,
                      label: 'Recurring Withdrawal Amount',
                      id: 'withdraw.source.recurringClientAmount',
                      disabled: withdrawalValues.source.reason === 'Max Tax Free Cash',
                    }}
                  />
                </Grid>
              )}
              {hasRecurring && (
                <Grid item xs={6}>
                  <AutoField
                    field={withdrawalStartField}
                    disabled={withdrawalValues.source.full}
                    helperText="Start date must be on 1st or 15th and at least 10 days in the future"
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item>
            <Grid container direction="row" spacing={2}>
              <Grid item xs={6}>
                <AutoField test="Full Redemption" field={withdrawalFullField} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </React.Fragment>
  );
};
